import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import ClassList from '../components/ClassList';
import ClassSelectionWizard from '../components/ClassSelectionWizard';
import Button from '../components/ui/Button';
import { Edit, Sparkles } from 'lucide-react';

const ClassesPage = () => {
  const { user, logout: authContextLogout, loading: authLoading } = useAuth(); 
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Wizard and personalization state
  const [showWizard, setShowWizard] = useState(true);
  const [userPreferences, setUserPreferences] = useState(null);
  const [personalizedClasses, setPersonalizedClasses] = useState([]);

  // Original state
  const [classType, setClassType] = useState('all');
  const [classes, setClasses] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user came from a direct link or has preferences stored
  useEffect(() => {
    // Check if user has URL parameters indicating they want to skip wizard
    const skipWizard = searchParams.get('skip_wizard') === 'true';
    const urlClassType = searchParams.get('class_type');
    
    if (skipWizard || urlClassType) {
      setShowWizard(false);
      if (urlClassType) {
        setClassType(urlClassType);
      }
    }
  }, [searchParams]);

  // Load available class types
  useEffect(() => {
    const loadClassTypes = async () => {
      try {
        const types = await api.classTypes.getActive();
        setClassTypes(types);
      } catch (err) {
        console.error('Error loading class types:', err);
      }
    };

    loadClassTypes();
  }, []);

  // Check admin status when user changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const adminStatus = await api.admin.verifyAdmin(user.$id);
          setIsAdmin(adminStatus);
        } catch (err) {
          console.error('Error checking admin status:', err);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Function to match user preferences to class types
  const matchClassesToPreferences = (allClasses, preferences) => {
    if (!preferences) return allClasses;

    const scored = allClasses.map(classData => {
      let score = 0;
      let matchReasons = [];

      // Language matching
      if (preferences.language === 'both' || 
          (preferences.language === 'mandarin' && classData.classTypeCategory?.includes('mandarin')) ||
          (preferences.language === 'cantonese' && classData.classTypeCategory?.includes('cantonese'))) {
        score += 3;
        matchReasons.push('language');
      }

      // Goals matching (check if class type category matches any of the user's goals)
      const classCategory = classData.classTypeCategory || '';
      preferences.goals.forEach(goal => {
        if (classCategory.includes(goal) || 
            (goal === 'conversation' && classCategory.includes('conversational')) ||
            (goal === 'business' && classCategory.includes('business')) ||
            (goal === 'exam_prep' && (classCategory.includes('exam') || classCategory.includes('hsk') || classCategory.includes('igcse')))) {
          score += 2;
          matchReasons.push(goal);
        }
      });

      // Background bonus (this could be refined based on class type names)
      const className = classData.classTypeName?.toLowerCase() || '';
      if (preferences.background === 'english_speaker' && className.includes('english speakers')) {
        score += 1;
        matchReasons.push('background');
      } else if (preferences.background === 'mandarin_speaker' && className.includes('mandarin speakers')) {
        score += 1;
        matchReasons.push('background');
      } else if (preferences.background === 'cantonese_speaker' && className.includes('cantonese speakers')) {
        score += 1;
        matchReasons.push('background');
      }

      return {
        ...classData,
        matchScore: score,
        matchReasons: [...new Set(matchReasons)],
        isPerfectMatch: score >= 4,
        isGoodMatch: score >= 2 && score < 4
      };
    });

    // Sort by match score, then by available spots
    return scored.sort((a, b) => {
      if (b.matchScore !== a.matchScore) {
        return b.matchScore - a.matchScore;
      }
      return b.spotsLeft - a.spotsLeft;
    });
  };

  // Fetch classes based on user preferences or class type
  const fetchClassesData = async (currentClassType = null, preferences = null) => {
    try {
      setLoading(true);
      setError(null);
      
      let availableClasses;
      
      if (preferences) {
        // Get all classes when we have preferences for matching
        availableClasses = await api.classes.getAvailable();
      } else if (currentClassType === 'all') {
        availableClasses = await api.classes.getAvailable();
      } else {
        availableClasses = await api.classes.getAvailable(currentClassType);
      }
      
      // Check enrollment status if user is logged in
      if (user && availableClasses) {
        const classesWithEnrollment = await Promise.all(
          availableClasses.map(async (classData) => {
            try {
              const isEnrolled = classData.members?.some(memberStr => {
                try {
                  const member = JSON.parse(memberStr);
                  return member.userId === user.$id;
                } catch (e) {
                  return false;
                }
              }) || false;
              
              return {
                ...classData,
                isEnrolled
              };
            } catch (err) {
              return {
                ...classData,
                isEnrolled: false
              };
            }
          })
        );
        
        // Apply personalization if preferences exist
        if (preferences) {
          const matchedClasses = matchClassesToPreferences(classesWithEnrollment, preferences);
          setPersonalizedClasses(matchedClasses);
        } else {
          setClasses(classesWithEnrollment);
        }
      } else {
        // Apply personalization if preferences exist
        if (preferences) {
          const matchedClasses = matchClassesToPreferences(availableClasses || [], preferences);
          setPersonalizedClasses(matchedClasses);
        } else {
          setClasses(availableClasses || []);
        }
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError('Failed to load classes. Please try again.');
      setClasses([]);
      setPersonalizedClasses([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle wizard completion
  const handleWizardComplete = async (preferences) => {
    setUserPreferences(preferences);
    setShowWizard(false);
    await fetchClassesData(null, preferences);
  };

  // Handle skip wizard
  const handleSkipWizard = () => {
    setShowWizard(false);
    fetchClassesData('all');
  };

  // Handle edit preferences
  const handleEditPreferences = () => {
    setShowWizard(true);
    setPersonalizedClasses([]);
  };

  // Handle browse all classes
  const handleBrowseAll = () => {
    setUserPreferences(null);
    setPersonalizedClasses([]);
    fetchClassesData('all');
  };

  // Load classes when not using wizard
  useEffect(() => {
    if (!showWizard && !userPreferences) {
      fetchClassesData(classType);
    }
  }, [classType, showWizard, userPreferences]);

  const handleClassTypeChange = (type) => {
    setClassType(type);
  };

  const handleJoinClass = (classData) => {
    if (!user) {
      navigate('/login', {
        state: {
          returnTo: '/confirmation',
          returnState: {
            classData,
            mode: 'join'
          }
        }
      });
      return;
    }

    navigate('/confirmation', {
      state: {
        classData,
        mode: 'join'
      }
    });
  };

  const handleLeaveClass = async (classData) => {
    if (!user) return;

    const confirmed = window.confirm(
      `Are you sure you want to leave the ${classData.classTypeName || 'class'} on ${classData.day} at ${classData.time}?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      
      const response = await api.classes.leave(classData.$id, user.$id);
      
      if (response.success) {
        // Refresh the classes list
        if (userPreferences) {
          await fetchClassesData(null, userPreferences);
        } else {
          await fetchClassesData(classType);
        }
        
        alert('You have successfully left the class.');
      } else {
        throw new Error(response.message || 'Failed to leave class');
      }
    } catch (err) {
      console.error('Error leaving class:', err);
      alert('Failed to leave class. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSetAvailability = (classTypeData) => {
    if (!user) {
      navigate('/login', {
        state: {
          returnTo: '/availability',
          returnState: { 
            classTypeId: classTypeData.$id,  // Pass the ID
            classType: classTypeData.category || classTypeData.name?.toLowerCase() 
          }
        }
      });
      return;
    }

    navigate('/availability', {
      state: { 
        classTypeId: classTypeData.$id,    // Pass the ID
        classType: classTypeData.category || classTypeData.name?.toLowerCase() 
      }
    });
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      if (authContextLogout) {
        authContextLogout();
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleManageClick = () => {
    navigate('/admin');
  };

  // Format preference summary
  const getPreferenceSummary = () => {
    if (!userPreferences) return '';
    
    const backgroundLabels = {
      'english_speaker': 'English Speaker',
      'mandarin_speaker': 'Mandarin Speaker',
      'cantonese_speaker': 'Cantonese Speaker',
      'multilingual': 'Multilingual'
    };
    
    const languageLabels = {
      'mandarin': 'Mandarin',
      'cantonese': 'Cantonese',
      'both': 'Both Languages'
    };
    
    const goalLabels = {
      'conversation': 'Conversation',
      'business': 'Business',
      'exam_prep': 'Exam Prep',
      'academic_support': 'Academic Support',
      'general': 'General'
    };
    
    const parts = [];
    
    if (userPreferences.background) {
      parts.push(`Background: ${backgroundLabels[userPreferences.background]}`);
    }
    if (userPreferences.language) {
      parts.push(`Language: ${languageLabels[userPreferences.language]}`);
    }
    if (userPreferences.goals.length > 0) {
      const goalsList = userPreferences.goals.map(g => goalLabels[g]).join(' + ');
      parts.push(`Goals: ${goalsList}`);
    }
    
    return parts.join(' • ');
  };

  // Get unique categories for tabs (when not using personalized view)
  const categories = [...new Set(classTypes.map(ct => ct.category).filter(Boolean))];
  const allCategories = ['all', ...categories];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Join a Group Class
            </span>
          </h1>
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <span className="text-gray-300 text-sm hidden sm:inline">
                  Hello, {user?.name || 'Guest'}
                </span>
                {isAdmin && (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleManageClick}
                    className="bg-purple-600 hover:bg-purple-700 border-purple-500"
                  >
                    Manage
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="small"
                  onClick={handleLogout}
                  className="border-gray-600 hover:bg-gray-700"
                >
                  Logout
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                size="small"
                onClick={handleLogin}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Login
              </Button>
            )}
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Show wizard */}
          {showWizard && (
            <>
              <div className="text-center mb-8">
                <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
                  欢迎
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  Find Your Perfect Class
                </h2>
                <div className="w-16 h-1 bg-purple-500 mx-auto mt-2 mb-4"></div>
                <p className="text-gray-400">
                  Let us help you find the perfect Chinese language class based on your background and goals.
                </p>
              </div>
              
              <ClassSelectionWizard 
                onComplete={handleWizardComplete}
                onSkip={handleSkipWizard}
              />
            </>
          )}

          {/* Show personalized results */}
          {!showWizard && userPreferences && personalizedClasses.length >= 0 && (
            <>
              {/* Personalized header */}
              <div className="text-center mb-6">
                <div className="inline-block text-4xl font-light text-purple-400 opacity-20 mb-2">
                  为您推荐
                </div>
                <h2 className="text-2xl font-bold mb-2">Your Recommended Classes</h2>
                <div className="w-16 h-1 bg-purple-500 mx-auto"></div>
              </div>

              {/* Preference summary with edit option */}
              <div className="bg-purple-600 bg-opacity-10 border border-purple-500 border-opacity-30 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles size={16} className="text-purple-400" />
                      <span className="text-purple-300 font-medium">Based on your preferences:</span>
                    </div>
                    <p className="text-purple-200 text-sm">{getPreferenceSummary()}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleEditPreferences}
                    className="text-purple-400 hover:text-purple-300 flex items-center"
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                </div>
              </div>

              {/* Personalized class list */}
              {loading ? (
                <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
                  <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-4 text-gray-400">Finding your perfect classes...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
                  <div className="p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg mb-4">
                    <p className="text-red-400">{error}</p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => fetchClassesData(null, userPreferences)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Try Again
                  </Button>
                </div>
              ) : (
                <PersonalizedClassList
                  classes={personalizedClasses}
                  onJoinClass={handleJoinClass}
                  onLeaveClass={handleLeaveClass}
                  onSetAvailability={handleSetAvailability}
                  onBrowseAll={handleBrowseAll}
                />
              )}
            </>
          )}

          {/* Show regular browsing interface */}
          {!showWizard && !userPreferences && (
            <>
              <div className="text-center mb-8">
                <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
                  选择课程
                </div>
                <h2 className="text-2xl font-bold">
                  Browse All Classes
                </h2>
                <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
                {!user && (
                  <p className="text-gray-400 text-sm mt-4">
                    Browse available classes below. You'll need to log in to join a class.
                  </p>
                )}
              </div>

              {/* Personalization prompt */}
              <div className="bg-purple-600 bg-opacity-10 border border-purple-500 border-opacity-30 rounded-lg p-6 mb-8">
                <div className="text-center">
                  <h3 className="text-xl font-medium text-purple-300 mb-2">
                    💡 Want us to recommend the perfect class for you?
                  </h3>
                  <p className="text-purple-200 mb-6">
                    Answer a few quick questions about your background and goals to get personalized recommendations.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      variant="primary"
                      onClick={() => setShowWizard(true)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Take the Quiz
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {/* This will just continue showing all classes below */}}
                      className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10"
                    >
                      Continue Browsing
                    </Button>
                  </div>
                </div>
              </div>

              {/* Regular class list */}
              {loading ? (
                <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
                  <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 814 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-4 text-gray-400">Loading classes...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
                  <div className="p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg mb-4">
                    <p className="text-red-400">{error}</p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => fetchClassesData(classType)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Try Again
                  </Button>
                </div>
              ) : (
                <ClassList
                  classes={classes}
                  classType={classType}
                  onJoinClass={handleJoinClass}
                  onLeaveClass={handleLeaveClass}
                  onSetAvailability={handleSetAvailability}
                />
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

// Personalized class list component
// Personalized class list component with updated CTAs
const PersonalizedClassList = ({ classes, onJoinClass, onLeaveClass, onSetAvailability, onBrowseAll, onEditPreferences }) => {
  const perfectMatches = classes.filter(c => c.isPerfectMatch);
  const goodMatches = classes.filter(c => c.isGoodMatch);
  const otherClasses = classes.filter(c => !c.isPerfectMatch && !c.isGoodMatch);

  // Helper function to convert time string to minutes for sorting
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let totalMinutes = (hours % 12) * 60 + minutes;
    if (period === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60;
    } else if (period === 'AM' && hours === 12) {
      totalMinutes = minutes; // 12 AM is 00:00
    }
    
    return totalMinutes;
  };

  // Group classes by day of the week
  const groupClassesByDay = (classList) => {
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const groupedClasses = {};
    
    // Initialize all days
    dayOrder.forEach(day => {
      groupedClasses[day] = [];
    });
    
    // Group classes by day
    classList.forEach(classData => {
      if (classData.day && groupedClasses[classData.day]) {
        groupedClasses[classData.day].push(classData);
      }
    });
    
    // Sort classes within each day by time (chronological order)
    dayOrder.forEach(day => {
      groupedClasses[day].sort((a, b) => {
        return timeToMinutes(a.time) - timeToMinutes(b.time);
      });
    });
    
    return dayOrder.reduce((result, day) => {
      result[day] = groupedClasses[day];
      return result;
    }, {});
  };

  // Helper function to get short day names
  const getShortDay = (day) => {
    const shortDays = {
      'Monday': 'Mon',
      'Tuesday': 'Tue', 
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    return shortDays[day] || day;
  };

  const renderCalendarSection = (title, classList, badgeColor, icon) => {
    if (classList.length === 0) return null;

    const groupedClasses = groupClassesByDay(classList);

    return (
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          {icon}
          <h3 className="text-lg font-semibold">{title}</h3>
          <span className={`px-2 py-1 text-xs rounded-full ${badgeColor}`}>
            {classList.length} class{classList.length !== 1 ? 'es' : ''}
          </span>
        </div>
        
        {/* Calendar layout - Days as columns */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-4">
            {Object.entries(groupedClasses).map(([day, dayClasses]) => (
              <div key={day} className="min-h-[200px]">
                {/* Day header */}
                <div className="text-center mb-4 pb-2 border-b border-gray-600">
                  <h4 className="text-base font-medium text-white">
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{getShortDay(day)}</span>
                  </h4>
                </div>
                
                {/* Classes for this day */}
                <div className="space-y-3">
                  {dayClasses.length > 0 ? (
                    dayClasses.map((classData) => (
                      <PersonalizedClassCard
                        key={classData.$id}
                        classData={classData}
                        onJoinClass={onJoinClass}
                        onLeaveClass={onLeaveClass}
                        showMatchBadge={title === "Perfect Matches"}
                        compact={true}
                      />
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-gray-500 text-sm">
                        No classes
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {classes.length > 0 ? (
        <>
          {/* Single calendar section for all recommended classes */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
            
            {/* Calendar layout - Days as columns */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-4">
              {Object.entries(groupClassesByDay(classes)).map(([day, dayClasses]) => (
                <div key={day} className="min-h-[200px]">
                  {/* Day header */}
                  <div className="text-center mb-4 pb-2 border-b border-gray-600">
                    <h4 className="text-base font-medium text-white">
                      <span className="hidden sm:inline">{day}</span>
                      <span className="sm:hidden">{getShortDay(day)}</span>
                    </h4>
                  </div>
                  
                  {/* Classes for this day */}
                  <div className="space-y-3">
                    {dayClasses.length > 0 ? (
                      dayClasses.map((classData) => (
                        <PersonalizedClassCard
                          key={classData.$id}
                          classData={classData}
                          onJoinClass={onJoinClass}
                          onLeaveClass={onLeaveClass}
                          showMatchBadge={false}
                          compact={true}
                        />
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-gray-500 text-sm">
                          No classes
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8 bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
          <p className="text-gray-300 mb-4">
            We couldn't find any classes that match your specific preferences at the moment.
          </p>
          <div className="space-y-4">
            <Button
              variant="primary"
              onClick={() => onSetAvailability('general')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Set My Availability
            </Button>
            <Button
              variant="outline"
              onClick={onBrowseAll}
              className="border-gray-600 hover:bg-gray-700"
            >
              Browse All Classes
            </Button>
          </div>
        </div>
      )}

      {/* Updated CTAs section - side by side cards */}
      <div className="space-y-6 pt-6 border-t border-gray-700">
        {/* Primary CTA - None of these times work */}
        <div className="bg-purple-600 bg-opacity-10 border border-purple-500 border-opacity-30 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-xl font-medium text-purple-300 mb-2">
              😕 None of these times work for you?
            </h3>
            <p className="text-purple-200 mb-6">
              Tell us when you're available and we'll notify you when a class forms at your preferred time.
            </p>
            <Button
              variant="primary"
              onClick={() => onSetAvailability('general')}
              className="bg-purple-600 hover:bg-purple-700 px-8 py-3 text-lg"
            >
              Set My Availability
            </Button>
          </div>
        </div>

        {/* Secondary CTAs - side by side */}
        <div className="text-center">
          <div className="text-gray-400 text-sm mb-4 relative">
            <span className="bg-gray-950 px-4">OR</span>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Browse all classes */}
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-6 hover:border-gray-500 transition-colors">
              <h4 className="font-medium text-white mb-2">
                🔍 Explore More Options
              </h4>
              <p className="text-gray-400 text-sm mb-4">
                Browse all available classes without personalization
              </p>
              <Button
                variant="outline"
                onClick={onBrowseAll}
                className="border-gray-500 text-gray-300 hover:bg-gray-700 w-full"
              >
                Browse All Classes
              </Button>
            </div>

            {/* Edit preferences */}
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-6 hover:border-gray-500 transition-colors">
              <h4 className="font-medium text-white mb-2">
                ⚙️ Refine Your Preferences
              </h4>
              <p className="text-gray-400 text-sm mb-4">
                Update your background, goals, or language preferences
              </p>
              <Button
                variant="outline"
                onClick={onEditPreferences}
                className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10 w-full"
              >
                Edit Preferences
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Personalized class card component
const PersonalizedClassCard = ({ classData, onJoinClass, onLeaveClass, showMatchBadge = false, compact = false }) => {
  const isJoinable = classData.status === 'active' && classData.spotsLeft > 0 && !classData.isEnrolled;
  const isWaitlistable = classData.status === 'full' && !classData.isEnrolled;
  const isEnrolled = classData.isEnrolled;

  if (compact) {
    // Compact version for calendar layout (similar to ClassList cards)
    return (
      <div className="bg-gray-700 bg-opacity-50 rounded-lg p-3 border border-gray-600 mb-3 transition-all duration-200 hover:bg-gray-600 hover:bg-opacity-50">
        {/* Time */}
        <div className="text-white font-medium text-base mb-2 text-center">
          {classData.time}
        </div>
        
        {/* Class Type Name */}
        <div className="text-center mb-2">
          <div className="text-purple-300 text-sm font-medium">
            {classData.classTypeName || 'Class'}
          </div>
        </div>
        
        {/* Enrolled badge if user is enrolled */}
        {isEnrolled && (
          <div className="mb-2 text-center">
            <span className="text-xs bg-green-500 bg-opacity-20 text-green-400 px-2 py-1 rounded-full font-medium">
              Enrolled
            </span>
          </div>
        )}
        
        {/* Progress bar */}
        {!isEnrolled && (
          <div className="mb-3">
            <div className="w-full bg-gray-800 rounded-full h-1.5 mb-1">
              <div
                className={`h-1.5 rounded-full ${
                  classData.status === 'cancelled' ? 'bg-red-500' :
                  classData.status === 'completed' ? 'bg-gray-500' :
                  'bg-[#7e55f6]'
                }`}
                style={{ width: `${classData.totalSpots > 0 ? ((classData.totalSpots - classData.spotsLeft) / classData.totalSpots) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="text-gray-400 text-xs text-center">
              {classData.spotsLeft > 0 && classData.status !== 'cancelled' && classData.status !== 'completed'
                ? `${classData.spotsLeft} spots`
                : (classData.status === 'full' || classData.spotsLeft === 0) && classData.status !== 'cancelled' && classData.status !== 'completed'
                  ? 'Full'
                  : classData.status === 'cancelled' ? 'Cancelled' :
                    classData.status === 'completed' ? 'Completed' : ''
              }
            </div>
          </div>
        )}

        {/* Enrolled message */}
        {isEnrolled && (
          <div className="mb-3 text-center">
            <div className="text-green-400 text-xs font-medium">
              See you there!
            </div>
          </div>
        )}
        
        {/* Action button */}
        <div className="flex justify-center">
          {isJoinable && (
            <Button
              variant="primary"
              size="small"
              onClick={() => onJoinClass(classData)}
              className="text-xs px-3 py-1.5"
            >
              Join
            </Button>
          )}
          {isWaitlistable && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => onJoinClass(classData)}
              className="text-xs px-3 py-1.5"
            >
              Waitlist
            </Button>
          )}
          {isEnrolled && (
            <Button
              variant="outline"
              size="small"
              onClick={() => onLeaveClass(classData)}
              className="border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10 hover:border-red-400 text-xs px-3 py-1.5"
            >
              Leave
            </Button>
          )}
          {!isJoinable && !isWaitlistable && !isEnrolled && (
            <span className="text-xs text-gray-400 px-2 py-1">
              {classData.status === 'cancelled' ? 'Cancelled' :
               classData.status === 'completed' ? 'Completed' :
               classData.status === 'pending' ? 'Coming Soon' :
               'Not Available'}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Full card version (if needed elsewhere)
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:border-gray-600 transition-all">
      {/* Match badge */}
      <div className="flex justify-between items-start mb-3">
        {isEnrolled && (
          <span className="px-2 py-1 bg-green-500 bg-opacity-20 text-green-400 rounded-full text-xs">
            Enrolled
          </span>
        )}
      </div>

      {/* Class info */}
      <div className="mb-4">
        <h4 className="font-medium text-white mb-1">
          {classData.classTypeName || 'Class'}
        </h4>
        <p className="text-gray-300 text-sm mb-2">
          {classData.day} at {classData.time}
        </p>
      </div>

      {/* Enrollment bar */}
      {!isEnrolled && (
        <div className="mb-4">
          <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
            <div
              className="bg-purple-500 h-2 rounded-full"
              style={{ width: `${classData.totalSpots > 0 ? ((classData.totalSpots - classData.spotsLeft) / classData.totalSpots) * 100 : 0}%` }}
            />
          </div>
          <p className="text-gray-400 text-xs">
            {classData.spotsLeft > 0 ? `${classData.spotsLeft} spots left` : 'Full'}
          </p>
        </div>
      )}

      {/* Action button */}
      <div className="flex justify-center">
        {isJoinable && (
          <Button
            variant="primary"
            size="small"
            onClick={() => onJoinClass(classData)}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            Join Class
          </Button>
        )}
        {isWaitlistable && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => onJoinClass(classData)}
            className="w-full"
          >
            Join Waitlist
          </Button>
        )}
        {isEnrolled && (
          <Button
            variant="outline"
            size="small"
            onClick={() => onLeaveClass(classData)}
            className="w-full border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10"
          >
            Leave Class
          </Button>
        )}
        {!isJoinable && !isWaitlistable && !isEnrolled && (
          <span className="text-xs text-gray-400 text-center py-2">
            {classData.status === 'cancelled' ? 'Cancelled' :
             classData.status === 'completed' ? 'Completed' : 'Not Available'}
          </span>
        )}
      </div>
    </div>
  );
};

export default ClassesPage;